var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class HitachiProjector {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.codeOffset = 0x30;
        this.crcMapToVideoInputs = {
            // CRC values from from the Hitachi video input command table
            0: [0xfe, 0xd2],
            1: [0x6e, 0xd3],
            2: [0x9e, 0xd3],
            3: [0x0e, 0xd2],
            4: [0x3e, 0xd0],
            5: [0xae, 0xd1],
            6: [0x5e, 0xd1],
            9: [0xae, 0xd4],
            10: [0x5e, 0xd4],
            11: [0xce, 0xd4],
            12: [0xfe, 0xd7],
            13: [0x6e, 0xd6],
            17: [0xae, 0xde],
            18: [0x5e, 0xde],
            19: [0xce, 0xdf], // Display Port
        };
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0xc8, 0xd8, 0x02, 0x00, 0x20, 0x30, 0x00, 0x00]), /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.displayBlank.value;
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const crc = blank ? [0x6b, 0xd9] : [0xfb, 0xd8];
                const match = yield this.socket.send(this.makeCommand([...crc, 0x01, 0x00, 0x20, 0x30, blank ? 0x01 : 0x00, 0x00]), /(\x06|\x15)|\x1C[\s\S]/, tags);
                return match[1] ? match[1].charCodeAt(0) === 6 : this.state.displayBlank.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.displayFreeze = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0xb0, 0xd2, 0x02, 0x00, 0x02, 0x30, 0x00, 0x00]), /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.displayFreeze.value;
            }),
            set: (freeze, tags) => __awaiter(this, void 0, void 0, function* () {
                const crc = freeze ? [0x13, 0xd3] : [0x83, 0xd2];
                const match = yield this.socket.send(this.makeCommand([...crc, 0x01, 0x00, 0x02, 0x30, freeze ? 0x01 : 0x00, 0x00]), /(\x06|\x15)|\x1C[\s\S]/, tags);
                return match[1] ? match[1].charCodeAt(0) === 6 : this.state.displayFreeze.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x19, 0xd3, 0x02, 0x00, 0x00, 0x60, 0x00, 0x00]), /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const crc = power ? [0xba, 0xd2] : [0x2a, 0xd3];
                this.state.busy.update(true);
                const match = yield this.socket.send(this.makeCommand([...crc, 0x01, 0x00, 0x00, 0x60, power ? 0x01 : 0x00, 0x00]), /(\x06|\x15)|\x1C[\s\S]/, tags);
                // When powering off, the fan runs for 77 seconds on our model and during that time you
                // can't turn it on.
                const delayMs = power ? 8000 : 77000;
                this.framework.suspend(delayMs);
                this.busyTimeout.set(() => this.state.busy.update(false), delayMs);
                return match[1] ? match[1].charCodeAt(0) === 6 : this.state.power.value;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0xcd, 0xd2, 0x02, 0x00, 0x00, 0x20, 0x00, 0x00]), /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/, tags);
                return match[1] !== undefined
                    ? String.fromCharCode(match[1].charCodeAt(0) + this.codeOffset)
                    : this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const newCode = code.charCodeAt(0) - this.codeOffset;
                if (this.crcMapToVideoInputs.hasOwnProperty(newCode)) {
                    const crc = this.crcMapToVideoInputs[newCode];
                    this.state.busy.update(true);
                    const match = yield this.socket.send(this.makeCommand([...crc, 0x01, 0x00, 0x00, 0x20, newCode, 0x00]), /(\x06|\x15)/, tags);
                    this.framework.suspend(3000);
                    this.busyTimeout.set(() => this.state.busy.update(false), 3000);
                    return match[1].charCodeAt(0) === 6 ? code : this.state.videoInput.value;
                }
                return this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                controls: ['displayBlank', 'displayFreeze', 'power', 'videoInput'],
            },
            {
                intervalMs: 1 * 60 * 60 * 1000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.getRunTime();
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const initSteps = [
                {
                    errorCode: 'verifyFailed',
                    send: this.makeCommand([0x19, 0xd3, 0x02, 0x00, 0x00, 0x60, 0x00, 0x00]),
                    next: /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/,
                },
            ];
            return yield CommandSocketInitializer.initialize(this.socket, initSteps, credentials);
        });
    }
    // other class methods
    getRunTime() {
        return __awaiter(this, void 0, void 0, function* () {
            let runTime = 0;
            if (this.power) {
                // high byte of runtime
                const matchHi = yield this.socket.send(this.makeCommand([0x2a, 0xfd, 0x02, 0x00, 0x9e, 0x10, 0x00, 0x00]), /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/, ['runTimeHigh']);
                if (matchHi[1]) {
                    runTime = matchHi[1].charCodeAt(0) << 8;
                }
                // Low byte of runtime
                const matchLo = yield this.socket.send(this.makeCommand([0xc2, 0xff, 0x02, 0x00, 0x90, 0x10, 0x00, 0x00]), /\x1D([\s\S])\x00|\x1C[\s\S]|\x15/, ['runTimeLow']);
                if (matchLo[1]) {
                    runTime += matchLo[1].charCodeAt(0);
                }
            }
            this.log.debug(`runTime = ${runTime}`);
            return runTime;
        });
    }
    makeCommand(data) {
        // add the header to the command data
        const bytes = [0xbe, 0xef, 0x03, 0x06, 0x00].concat(data);
        return AsciiText.decode(bytes);
    }
}
// static properties
HitachiProjector.driverType = 'socketDriver';
HitachiProjector.key = '-LiTDmBRVZlC0Mm2IevZ';
HitachiProjector.baseSocketOptions = {
    commandExtractor: {
        unterminatedRegExp: /\x1D[\s\S]{2}|\x1C[\s\S]|\x15|\x06/,
        terminators: [],
    },
    isBinary: true,
    sendTerminator: '',
};
HitachiProjector.socketOptions = [
    Object.assign(Object.assign({}, HitachiProjector.baseSocketOptions), { port: 23 }),
    Object.assign(Object.assign({}, HitachiProjector.baseSocketOptions), { port: 4999 }),
];
